import React, { useEffect, useMemo, useState } from "react";
import { Search, Plus, Minus, Loader2, Star, Heart, Sparkles, ShoppingBasket } from "lucide-react";
import { api, mediaUrl } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import MarketDayBanner from "../../components/MarketDayBanner";
import { HERO_IMAGE, STOCK_BADGE, gbp } from "../../lib/constants";

export default function Home() {
  const { items, addItem, updateQuantity } = useCart();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState(null);
  const [favs, setFavs] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/customer/products"),
      api.get("/customer/categories"),
      api.get("/customer/market-day/status"),
      api.get("/customer/favourites").catch(() => ({ data: [] })),
    ])
      .then(([p, c, s, f]) => {
        setProducts(p.data);
        setCategories(c.data);
        setStatus(s.data);
        setFavs(f.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const qtyOf = (id) => items.find((i) => i.product_id === id)?.quantity || 0;
  const isFav = (id) => favs.includes(id);

  const toggleFav = async (id) => {
    const currently = isFav(id);
    setFavs((prev) => (currently ? prev.filter((x) => x !== id) : [...prev, id]));
    try {
      if (currently) await api.delete(`/customer/favourites/${id}`);
      else await api.put(`/customer/favourites/${id}`);
    } catch (e) {
      setFavs((prev) => (currently ? [...prev, id] : prev.filter((x) => x !== id)));
    }
  };

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchCat =
        activeCat === "all" ||
        (activeCat === "favs" ? favs.includes(p.id) : p.category_id === activeCat);
      const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [products, activeCat, search, favs]);

  const featured = useMemo(() => products.filter((p) => p.is_featured), [products]);

  const open = status?.is_open;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6">
      {/* Hero */}
      <div className="pt-4 md:pt-8">
        <div className="relative rounded-3xl overflow-hidden h-40 md:h-56">
          <img src={HERO_IMAGE} alt="Ayome market" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F13]/85 to-[#0A1F13]/30" />
          <div className="relative h-full flex flex-col justify-center px-6 md:px-10 text-white">
            <p className="text-xs md:text-sm uppercase tracking-widest text-[#7CFC7C] font-semibold">Ayome Groceries</p>
            <h1 className="font-display text-2xl md:text-4xl font-bold mt-1 max-w-md leading-tight">
              Nigerian flavours, freshly sourced.
            </h1>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <MarketDayBanner status={status} />
      </div>

      {/* Featured Picks */}
      {!search && activeCat === "all" && featured.length > 0 && (
        <div data-testid="featured-row" className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-[#FF9500]" />
            <h2 className="font-display text-xl font-bold text-[#0A1F13]">Featured Picks</h2>
          </div>
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
            {featured.map((p) => {
              const qty = qtyOf(p.id);
              const out = p.stock_status === "out_of_stock";
              const badge = STOCK_BADGE[p.stock_status];
              return (
                <div key={p.id} data-testid={`featured-${p.id}`} className="shrink-0 w-44 rounded-2xl bg-gradient-to-b from-[#E8F5E9] to-white border border-[#BBF7D0] p-3 shadow-[0_4px_20px_rgb(34,139,34,0.08)]">
                  <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                    <Thumb product={p} out={out} />
                    <button
                      data-testid={`featured-fav-${p.id}`}
                      onClick={() => toggleFav(p.id)}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm transition-transform duration-200 active:scale-90"
                    >
                      <Star size={16} className={isFav(p.id) ? "fill-[#FF9500] text-[#FF9500]" : "text-gray-400"} />
                    </button>
                    {badge && (
                      <span className={`absolute bottom-2 left-2 text-[10px] font-bold px-2 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-[15px] text-[#0A1F13] leading-tight line-clamp-3 mt-2.5">{p.name}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">{gbp(p.price)} <span className="text-gray-400">/ {p.unit}</span></p>
                  <div className="mt-2">
                    {out ? (
                      <button disabled className="w-full h-9 rounded-full bg-gray-100 text-gray-400 text-sm font-semibold">Out of Stock</button>
                    ) : qty === 0 ? (
                      <button
                        data-testid={`featured-add-${p.id}`}
                        disabled={!open}
                        onClick={() => addItem(p)}
                        className="w-full h-9 rounded-full bg-[#228B22] hover:bg-[#1A4331] text-white text-sm font-semibold flex items-center justify-center gap-1 transition-colors duration-200 disabled:bg-gray-200 disabled:text-gray-400"
                      >
                        <Plus size={16} /> Add
                      </button>
                    ) : (
                      <div className="flex items-center justify-between h-9 rounded-full bg-white border border-[#BBF7D0] px-1">
                        <button onClick={() => updateQuantity(p.id, qty - 1)} className="w-7 h-7 rounded-full bg-[#E8F5E9] flex items-center justify-center text-[#1A4331]"><Minus size={15} /></button>
                        <span className="font-semibold text-[#1A4331]">{qty}</span>
                        <button onClick={() => updateQuantity(p.id, qty + 1)} className="w-7 h-7 rounded-full bg-[#228B22] text-white flex items-center justify-center"><Plus size={15} /></button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mt-5 relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          data-testid="product-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search groceries…"
          className="w-full h-12 pl-11 pr-4 rounded-full bg-white border border-gray-200 focus:border-[#228B22] outline-none transition-colors shadow-[0_2px_12px_rgb(0,0,0,0.03)]"
        />
      </div>

      {/* Categories */}
      <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar pb-1">
        <Chip label="All" active={activeCat === "all"} onClick={() => setActiveCat("all")} testid="cat-all" />
        <button
          data-testid="cat-favourites"
          onClick={() => setActiveCat("favs")}
          className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 flex items-center gap-1.5 ${
            activeCat === "favs"
              ? "bg-[#1A4331] text-white border-[#1A4331]"
              : "bg-white text-gray-700 border-gray-200 hover:border-[#228B22]"
          }`}
        >
          <Star size={14} className={activeCat === "favs" ? "fill-[#7CFC7C] text-[#7CFC7C]" : "fill-[#FF9500] text-[#FF9500]"} />
          My Usuals
        </button>
        {categories.map((c) => (
          <Chip key={c.id} label={c.name} active={activeCat === c.id} onClick={() => setActiveCat(c.id)} testid={`cat-${c.slug}`} />
        ))}
      </div>

      {/* Products */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-[#228B22]" size={32} />
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="no-products" className="text-center py-20">
          {activeCat === "favs" ? (
            <>
              <Heart size={34} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-semibold">No favourites yet</p>
              <p className="text-gray-400 text-sm mt-1">Tap the star on any item to save your usuals.</p>
            </>
          ) : (
            <p className="text-gray-400">No products found.</p>
          )}
        </div>
      ) : (
        <div data-testid="product-grid" className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {filtered.map((p) => {
            const qty = qtyOf(p.id);
            const out = p.stock_status === "out_of_stock";
            const badge = STOCK_BADGE[p.stock_status];
            return (
              <div key={p.id} data-testid={`product-${p.id}`} className="group">
                <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100">
                  <Thumb product={p} out={out} hover />
                  <button
                    data-testid={`fav-toggle-${p.id}`}
                    onClick={() => toggleFav(p.id)}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm transition-transform duration-200 active:scale-90"
                    aria-label="Toggle favourite"
                  >
                    <Star size={16} className={isFav(p.id) ? "fill-[#FF9500] text-[#FF9500]" : "text-gray-400"} />
                  </button>
                  {badge && (
                    <span data-testid={`stock-badge-${p.id}`} className={`absolute bottom-2 left-2 text-[10px] font-bold px-2 py-1 rounded-full ${badge.cls}`}>
                      {badge.label}
                    </span>
                  )}
                </div>
                <div className="mt-2.5 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-[15px] text-[#0A1F13] leading-tight line-clamp-3">{p.name}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {gbp(p.price)} <span className="text-gray-400">/ {p.unit}</span>
                    </p>
                  </div>
                </div>
                <div className="mt-2">
                  {out ? (
                    <button disabled className="w-full h-9 rounded-full bg-gray-100 text-gray-400 text-sm font-semibold">Out of Stock</button>
                  ) : qty === 0 ? (
                    <button
                      data-testid={`add-to-cart-${p.id}`}
                      disabled={!open}
                      onClick={() => addItem(p)}
                      className="w-full h-9 rounded-full bg-[#228B22] hover:bg-[#1A4331] text-white text-sm font-semibold flex items-center justify-center gap-1 transition-colors duration-200 disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      <Plus size={16} /> Add
                    </button>
                  ) : (
                    <div className="flex items-center justify-between h-9 rounded-full bg-[#E8F5E9] px-1">
                      <button data-testid={`decrease-${p.id}`} onClick={() => updateQuantity(p.id, qty - 1)} className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-[#1A4331]"><Minus size={15} /></button>
                      <span data-testid={`qty-${p.id}`} className="font-semibold text-[#1A4331]">{qty}</span>
                      <button data-testid={`increase-${p.id}`} onClick={() => updateQuantity(p.id, qty + 1)} className="w-7 h-7 rounded-full bg-[#228B22] text-white flex items-center justify-center"><Plus size={15} /></button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="h-8" />
    </div>
  );
}

function Chip({ label, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
        active ? "bg-[#1A4331] text-white border-[#1A4331]" : "bg-white text-gray-700 border-gray-200 hover:border-[#228B22]"
      }`}
    >
      {label}
    </button>
  );
}

function Thumb({ product, out, hover }) {
  const src = mediaUrl(product.image_url) || product.image_base64;
  if (src) {
    return (
      <img
        src={src}
        loading="lazy"
        alt={product.name}
        className={`w-full h-full object-cover ${hover ? "transition-transform duration-300 group-hover:scale-105" : ""} ${out ? "opacity-60" : ""}`}
      />
    );
  }
  return (
    <div className={`w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#E8F5E9] to-[#D3EAD5] px-2 ${out ? "opacity-60" : ""}`}>
      <ShoppingBasket size={28} className="text-[#228B22]/50 mb-1.5" strokeWidth={1.8} />
      <span className="text-[11px] font-semibold text-[#1A4331]/70 text-center leading-tight line-clamp-2">
        {product.name}
      </span>
    </div>
  );
}
